/**
 * GSTIN input with local structural validation (state code + PAN pattern +
 * check-digit) — no external GST-portal call, per user decision: India's
 * GSTN has no free public API for real company-detail lookup, and a paid
 * aggregator wasn't wanted yet. Catches the far more common failure mode
 * (a mistyped digit) immediately instead of at e-way-bill/invoice time.
 *
 * Usage: <GstinField label="Party GSTIN" value={form.party_gstin}
 *          onChange={set('party_gstin')} onStateResolved={(name) => ...} />
 * onStateResolved fires with the decoded state name when the GSTIN is valid —
 * wire it to auto-fill a paired state / place-of-supply field.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { apiGet } from '../lib/api';

export default function GstinField({ label = 'GSTIN', value, onChange, onStateResolved, required = false }) {
  const [result, setResult] = useState(null);   // { valid, errors, state_name, ... }
  const [checking, setChecking] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const v = (value || '').trim();
    if (v.length < 15) { setResult(null); setChecking(false); return; }
    setChecking(true);
    debounceRef.current = setTimeout(() => {
      apiGet('/gst/validate', { gstin: v })
        .then((r) => {
          setResult(r);
          if (r.valid && r.state_name) onStateResolved?.(r.state_name);
        })
        .catch(() => setResult(null))
        .finally(() => setChecking(false));
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div>
      <label className="label">{label}{required ? ' *' : ''}</label>
      <div className="relative">
        <input value={value} onChange={(e) => onChange({ target: { value: e.target.value.toUpperCase() } })}
          className={`input pr-8 ${result && !checking ? (result.valid ? 'border-emerald-400' : 'border-rose-400') : ''}`}
          maxLength={15} placeholder="27AAPFU0939F1ZV" data-testid="gstin-input" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2">
          {checking && <Loader2 size={14} className="animate-spin text-slate-400" />}
          {!checking && result?.valid && <CheckCircle2 size={14} className="text-emerald-500" />}
          {!checking && result && !result.valid && <AlertCircle size={14} className="text-rose-500" />}
        </span>
      </div>
      {!checking && result?.valid && (
        <div className="text-[11px] text-emerald-600 mt-0.5">{result.state_name} · PAN {result.pan}</div>
      )}
      {!checking && result && !result.valid && (
        <div className="text-[11px] text-rose-600 mt-0.5">{result.errors[0]}</div>
      )}
    </div>
  );
}
